import React, { useEffect, useState } from 'react';
import { Save, X } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  PATIENT_CLINICAL_EVENT_LABELS,
  PATIENT_CLINICAL_EVENT_TYPE_OPTIONS,
  PatientClinicalTimelineService,
  type PatientClinicalEvent,
  type PatientClinicalEventSeverity,
  type PatientClinicalEventType,
} from '../../services/PatientClinicalTimelineService';
import { PatientLifecycleService } from '../../services/PatientLifecycleService';

export type EditableClinicalEvent = Pick<
  PatientClinicalEvent,
  'eventId' | 'eventType' | 'severity' | 'eventTitle' | 'eventDescription' | 'eventDate'
>;

interface ClinicalEventModalProps {
  isOpen: boolean;
  patientId: string;
  clientId: string;
  patientName?: string;
  userId: string;
  userDisplayName?: string | null;
  eventToEdit?: EditableClinicalEvent | null;
  onClose: () => void;
  onSaved?: () => void | Promise<void>;
}

const toLocalDateTimeInput = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const dateToLocalDateTimeInput = (value: string) => {
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const createInitialForm = (eventToEdit?: EditableClinicalEvent | null) =>
  eventToEdit
    ? {
        eventType: eventToEdit.eventType,
        severity: eventToEdit.severity,
        eventTitle: eventToEdit.eventTitle,
        eventDescription: eventToEdit.eventDescription,
        eventDate: dateToLocalDateTimeInput(eventToEdit.eventDate),
      }
    : {
        eventType: 'complication' as PatientClinicalEventType,
        severity: 'moderate' as PatientClinicalEventSeverity,
        eventTitle: PATIENT_CLINICAL_EVENT_LABELS.complication,
        eventDescription: '',
        eventDate: toLocalDateTimeInput(),
      };

const ClinicalEventModal: React.FC<ClinicalEventModalProps> = ({
  isOpen,
  patientId,
  clientId,
  patientName,
  userId,
  userDisplayName,
  eventToEdit,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState(() => createInitialForm(eventToEdit));
  const [submitting, setSubmitting] = useState(false);
  const [deactivatePatient, setDeactivatePatient] = useState(false);
  const isEditing = Boolean(eventToEdit);

  useEffect(() => {
    if (isOpen) {
      setForm(createInitialForm(eventToEdit));
      setDeactivatePatient(false);
    }
  }, [eventToEdit, isOpen, patientId]);

  if (!isOpen) return null;

  const selectEventType = (eventType: PatientClinicalEventType) => {
    const option = PATIENT_CLINICAL_EVENT_TYPE_OPTIONS.find((item) => item.value === eventType);
    if (!option) return;
    setForm((current) => ({
      ...current,
      eventType,
      severity: option.defaultSeverity,
      eventTitle: option.label,
    }));
    if (eventType !== 'treatment_discharge') setDeactivatePatient(false);
  };

  const saveEvent = async () => {
    if (!form.eventTitle.trim() || !form.eventDescription.trim() || !form.eventDate) {
      toast.error('Informe título, descrição e data do evento.');
      return;
    }

    setSubmitting(true);
    try {
      if (eventToEdit) {
        await PatientClinicalTimelineService.updateEvent({
          eventId: eventToEdit.eventId,
          clientId,
          patientId,
          ...form,
        });
      } else {
        await PatientClinicalTimelineService.createEvent({
          clientId,
          patientId,
          createdByUserId: userId,
          createdByDisplayName: userDisplayName,
          ...form,
        });
      }

      if (isTreatmentDischarge && deactivatePatient) {
        try {
          await PatientLifecycleService.setPatientCareActive(patientId, clientId, false);
        } catch (deactivationError) {
          console.error('Alta salva, mas não foi possível inativar o paciente:', deactivationError);
          toast.warning(
            'A alta foi salva na timeline, mas não foi possível inativar o paciente. Tente novamente editando este registro.',
          );
          onClose();
          await onSaved?.();
          return;
        }
      }

      toast.success(
        isTreatmentDischarge && deactivatePatient
          ? 'Alta registrada e paciente inativado para novos atendimentos.'
          : isEditing
            ? 'Registro clínico atualizado.'
            : 'Registro adicionado à timeline do paciente.',
      );
      onClose();
      await onSaved?.();
    } catch (error: unknown) {
      console.error('Erro ao salvar evento clínico:', error);
      const code =
        typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
      toast.error(
        code === '23514'
          ? 'Este tipo de evento ainda não está habilitado no banco de dados da clínica.'
          : isEditing
            ? 'Não foi possível atualizar o evento clínico.'
            : 'Não foi possível salvar o evento clínico.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isTreatmentDischarge = form.eventType === 'treatment_discharge';
  const isHospitalDischarge = form.eventType === 'hospital_discharge';

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 p-3 sm:p-4">
      <div className="max-h-[94vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 p-4 sm:p-5">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              {isEditing ? 'Editar registro clínico' : 'Novo registro clínico'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {patientName || 'Paciente selecionado'} · este registro não será uma evolução.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label={isEditing ? 'Fechar edição do registro clínico' : 'Fechar novo registro clínico'}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
            Registre acontecimentos relevantes, como hospitalização, exacerbação, alta ou intercorrência.
            O registro será exibido na timeline clínica do paciente e não será incluído no texto desta evolução.
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Tipo de registro</label>
              <select
                value={form.eventType}
                onChange={(event) => selectEventType(event.target.value as PatientClinicalEventType)}
                className="w-full rounded-lg border-slate-300"
              >
                {PATIENT_CLINICAL_EVENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Relevância</label>
              <select
                value={form.severity}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    severity: event.target.value as PatientClinicalEventSeverity,
                  }))
                }
                className="w-full rounded-lg border-slate-300"
              >
                <option value="low">Baixa</option>
                <option value="moderate">Moderada</option>
                <option value="high">Alta</option>
              </select>
            </div>
          </div>

          {isTreatmentDischarge && (
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-teal-200 bg-teal-50 px-3 py-3 text-sm text-teal-950">
              <input
                type="checkbox"
                checked={deactivatePatient}
                onChange={(event) => setDeactivatePatient(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-teal-300 text-teal-700 focus:ring-teal-600"
              />
              <span>
                <span className="block font-semibold">Também inativar para novos atendimentos</span>
                <span className="mt-1 block text-xs leading-5 text-teal-800">
                  Impede novos agendamentos, evoluções e avaliações. Não cancela sessões existentes nem altera
                  faturas, pacotes ou o histórico do paciente.
                </span>
              </span>
            </label>
          )}

          {isHospitalDischarge && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
              A alta hospitalar registra apenas o evento e não altera o status assistencial do paciente.
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Data e hora</label>
            <input
              type="datetime-local"
              value={form.eventDate}
              onChange={(event) => setForm((current) => ({ ...current, eventDate: event.target.value }))}
              className="w-full rounded-lg border-slate-300"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Título</label>
            <input
              value={form.eventTitle}
              onChange={(event) => setForm((current) => ({ ...current, eventTitle: event.target.value }))}
              placeholder="Resumo curto do evento"
              className="w-full rounded-lg border-slate-300"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Descrição objetiva</label>
            <textarea
              value={form.eventDescription}
              onChange={(event) =>
                setForm((current) => ({ ...current, eventDescription: event.target.value }))
              }
              rows={5}
              placeholder="Descreva o que aconteceu, o impacto observado e a conduta tomada, se houver."
              className="w-full rounded-lg border-slate-300"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 p-4 sm:flex-row sm:justify-end sm:p-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void saveEvent()}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {submitting ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Salvar registro'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClinicalEventModal;
